import type { AllFeatures, WorkbookUiFeatureName } from 'cadenza/features';
import { getDependentFeatures, isSingleFeatureAvailable, isWorkbookFeature } from 'cadenza/features';
import type { RootStateOr } from 'cadenza/store/cadenza-store';
import { isOperationModeSimplified } from 'cadenza/workbook/operation-mode';
import { slice } from 'cadenza/workbook/workbook-core-slice.state';
import { getSetting, isSettingSet } from 'cadenza/workbook/workbook-settings-util';
import type { WorkbookState } from 'cadenza/workbook/workbook.state';

//exported only for testing
export function __setSimplifiedModeFeatures__(featureDefaults: Partial<Record<WorkbookUiFeatureName, boolean>>) {
  SIMPLIFIED_MODE_FEATURE_DEFAULTS = featureDefaults;
}

let SIMPLIFIED_MODE_FEATURE_DEFAULTS: Partial<Record<WorkbookUiFeatureName, boolean>> = {
  'workbook-analysis-context': true,
  'workbook-analysis-context-closeable': true,
  'workbook-condition-management': false,
  'workbook-data-browser': false,
  'workbook-data-view-analysis-context': false,
  'workbook-data-view-content': false,
  'workbook-design': true,
  'workbook-drillthrough-external-links': true,
  'workbook-drillthrough-other-workbooks': true,
  'workbook-drillthrough-postmessage': true,
  'workbook-drillthrough-same-workbook': true,
  'workbook-layout-management': false,
  'workbook-management': false,
  'workbook-map-view-sidebar-resize': true,
  'workbook-map-add-layer-from-catalog': true,
  'workbook-map-add-layer-from-geodata-service': true,
  'workbook-map-add-data-view-layer': true,
  'workbook-map-add-grid-square-layer': true,
  'workbook-map-add-group-layer': true,
  'workbook-map-add-layer-from-data-import': true,
  'workbook-map-add-sketch-layer': true,
  'workbook-map-add-data-acquisition-layer': true,
  'workbook-map-add-analytics-extension-layer': true,
  'workbook-map-data-acquisition': false,
  'workbook-map-find-address': true,
  'workbook-map-find-coordinate': true,
  'workbook-map-find-what3words': true,
  'workbook-map-isochrones': true,
  'workbook-map-location-finder': true,
  'workbook-map-measure': true,
  'workbook-map-routing': true,
  'workbook-map-timeline-3d': true,
  'workbook-map-timeline-player': true,
  'workbook-map-toolbar': true,
  'workbook-pdf-export': true,
  'workbook-print-worksheet': true,
  'workbook-refresh-worksheet': true,
  'workbook-show-filtered-views': false,
  'workbook-table-cell-details': true,
  'workbook-table-single-measure-values': true,
  'workbook-tables-excel-export': true,
  'workbook-view-data-and-images-export': true,
  'workbook-view-management': false,
  'workbook-view-pdf-export': true,
  'workbook-worksheet-menu': true,
  'workbook-worksheet-pdf-export': true,
  'workbook-worksheet-tables-excel-export': true,
};

export function isWorkbookFeatureAvailable(state: RootStateOr<WorkbookState>, feature: AllFeatures): boolean {
  return (
    isSingleWorkbookFeatureAvailable(state, feature) &&
    getDependentFeatures(feature).every((dependentFeature) => isWorkbookFeatureAvailable(state, dependentFeature))
  );
}

function isSingleWorkbookFeatureAvailable(state: RootStateOr<WorkbookState>, feature: AllFeatures) {
  return (
    isSingleFeatureAvailable(feature) &&
    !(isWorkbookFeature(feature) && isDisabledBecauseOfSimplifiedMode(state, feature))
  );
}

export function isDisabledBecauseOfSimplifiedMode(state: RootStateOr<WorkbookState>, feature: WorkbookUiFeatureName) {
  return isOperationModeSimplified(state) && !isEnabledInSimplifiedMode(state, feature);
}

export function isEnabledInSimplifiedMode(state: RootStateOr<WorkbookState>, feature: WorkbookUiFeatureName) {
  const setting = getSimplifiedModeSetting(feature);
  return setting == null || getSetting(slice(state), setting, SIMPLIFIED_MODE_FEATURE_DEFAULTS[feature]!);
}

export function isSimplifiedModeSettingChanged(state: RootStateOr<WorkbookState>, feature: WorkbookUiFeatureName) {
  const setting = getSimplifiedModeSetting(feature);
  return setting != null && isSettingSet(slice(state), setting);
}

export function getSimplifiedModeSetting(feature: WorkbookUiFeatureName) {
  if (feature in SIMPLIFIED_MODE_FEATURE_DEFAULTS) {
    return `workbook.simplified.${feature}`;
  }
}
